import { useCanvasStore } from '../store/canvas-store';
import { worldToScreen } from '../canvas/viewport';
import { usePresenceCtx } from './PresenceProvider';
import type { PresencePeer } from './types';

/**
 * Owner-colored outline + "name · editing" tag on any frame a remote peer has
 * selected. Drawn UNDER the local accent (the local selection signal stays
 * loudest) and below interactive chrome. Screen-space, mapped through the local
 * viewport.
 */
export function RemoteSelection() {
  const { others } = usePresenceCtx();
  const frames = useCanvasStore((s) => s.doc.frames);
  const viewport = useCanvasStore((s) => s.doc.viewport);

  const tags = others
    .filter((p) => p.selection)
    .map((p) => {
      const f = frames.find((fr) => fr.id === p.selection);
      return f ? { peer: p, frame: f } : null;
    })
    .filter((x): x is { peer: PresencePeer; frame: (typeof frames)[number] } => x !== null);

  if (tags.length === 0) return null;

  return (
    <div className="pointer-events-none fixed inset-0 z-[54]" aria-hidden>
      {tags.map(({ peer, frame }) => {
        const tl = worldToScreen({ x: frame.x, y: frame.y }, viewport);
        const w = frame.w * viewport.zoom;
        const h = (frame.h ?? 200) * viewport.zoom;
        return (
          <div
            key={peer.id}
            className="absolute left-0 top-0"
            style={{ transform: `translate3d(${tl.x}px, ${tl.y}px, 0)`, width: w, height: h }}
          >
            <div className="absolute inset-0 rounded-[6px]" style={{ outline: `2px solid ${peer.color}`, outlineOffset: '1px' }} />
            <span
              className="absolute left-0 -top-[20px] whitespace-nowrap rounded-[6px] px-1.5 py-0.5 text-[11px] font-medium text-white"
              style={{ background: peer.color }}
            >
              {peer.name} · editing
            </span>
          </div>
        );
      })}
    </div>
  );
}
